import { useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/components/Auth/AuthProvider'
import { useToast } from '@/components/common/Toast'
import { Eye, EyeOff } from 'lucide-react'
import { LanguagePicker } from '@/components/common/LanguagePicker'
import { GeoNexoraLogo } from '@/components/common/GeoNexoraLogo'
import './styles/LoginPage.css'

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { login, ums, signInWithUms, companies, chooseCompany, signInError, isAuthenticated } = useAuth()
  const { addToast } = useToast()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      if (!username || !password) {
        setError(t('validation.required'))
        addToast(t('validation.required'), 'error')
        setIsLoading(false)
        return
      }

      await login(username, password)
      addToast(t('messages.login_success'), 'success')

      const from = location.state?.from?.pathname || '/'
      navigate(from, { replace: true })
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || t('errors.invalid_credentials')
      setError(errorMessage)
      addToast(errorMessage, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  if (ums && isAuthenticated) {
    return <Navigate to={location.state?.from?.pathname || '/'} replace />
  }

  return (
    <div className="min-h-screen bg-gn-primary flex items-center justify-center px-4">
      {/* Language Switcher */}
      <div className="absolute top-6 right-6">
        <LanguagePicker />
      </div>

      {/* Login Card */}
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <GeoNexoraLogo size={40} />
            <h1 className="text-3xl font-bold text-gn-primary">GeoNexora</h1>
          </div>
          <p className="text-gn-blue">{t('app.subtitle')}</p>
        </div>

        {/* Card */}
        <div className="bg-gn-surface rounded-lg shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-gn-primary mb-2">{t('menu.login')}</h2>
          {!ums && <p className="text-gn-muted mb-6">{t('messages.admin_access')}</p>}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-gn-danger-light border border-gn-danger rounded-lg">
              <p className="text-sm text-gn-danger">{error}</p>
            </div>
          )}

          {/* Sign-in through UMS (auth-config.json says so): the identity provider, then the company. */}
          {ums && (
            <div className="space-y-4">
              {signInError && (
                <div className="p-4 bg-gn-danger-light border border-gn-danger rounded-lg">
                  <p className="text-sm text-gn-danger">
                    {t(`signin.${signInError}`, { defaultValue: t('signin.failed') })}
                  </p>
                </div>
              )}
              {companies ? (
                <div>
                  <p className="text-sm text-gn-secondary mb-2">{t('signin.choose_company')}</p>
                  <ul className="space-y-2">
                    {companies.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => chooseCompany(c.id)}
                          className="w-full px-4 py-2 border border-gn-light rounded-lg text-left hover:bg-gn-blue hover:text-gn-inverse transition-colors"
                        >
                          {c.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => signInWithUms()}
                  className="w-full mt-2 px-4 py-2 bg-gn-blue text-gn-inverse font-medium rounded-lg hover:bg-gn-blue transition-colors flex items-center justify-center gap-2"
                >
                  {t('signin.button')}
                </button>
              )}
            </div>
          )}

          {/* Login Form */}
          {!ums && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gn-secondary mb-1">
                {t('admin.username')}
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('admin.username')}
                className="w-full px-4 py-2 border border-gn-light rounded-lg focus:ring-2 focus:ring-gn-blue focus:border-transparent outline-none transition"
                disabled={isLoading}
                autoFocus
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gn-secondary mb-1">
                {t('admin.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('admin.password')}
                  className="w-full px-4 py-2 border border-gn-light rounded-lg focus:ring-2 focus:ring-gn-blue focus:border-transparent outline-none transition"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gn-secondary hover:text-gn-muted"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <input
                id="remember"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-gn-blue rounded border-gn-light"
                disabled={isLoading}
              />
              <label htmlFor="remember" className="ml-2 text-sm text-gn-muted">
                {t('messages.remember_me')}
              </label>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full mt-6 px-4 py-2 bg-gn-blue text-gn-inverse font-medium rounded-lg hover:bg-gn-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
              {isLoading ? t('messages.loading') : t('menu.login')}
            </button>
          </form>
          )}

          {/* Footer */}
          <p className="text-center text-sm text-gn-muted mt-6">
            GeoNexora v1.0.0
          </p>
        </div>
      </div>
    </div>
  )
}
